/**
 * useArtifiPreviewBudget -- read the shared Artifi 90% budget gate.
 *
 * ADR 0065 amendment (real Artifi previews on the Decorations page), Jake's
 * 90% budget gate (2026-08-04). The Decorations page fans out one preview per
 * placement; before spending ANY Artifi call it checks whether the shared
 * monthly meter is at/over 90% via GET /api/artifi/preview_budget. When it is,
 * the page renders the schematic (ADR 0065 original) with a "live previews
 * temporarily limited" note -- reserving the last 10% for the money-path
 * surfaces (cart, designer, order proof).
 *
 * Module-cached: the budget is a page-wide fact, so a single fetch is shared
 * by every GarmentRenderLayer on the page (one status call, not one per
 * placement). This is a status read, not a render -- it does NOT consume the
 * Artifi cap.
 *
 * FAIL-OPEN: any fetch error leaves ``overBudget`` false. Never hide live
 * previews because the status call blipped -- the server-side gate
 * (source=decorations_page -> budget_exceeded) is the authoritative backstop,
 * so a false-open here at worst lets a single render attempt through, which
 * the server then gates.
 *
 * Public surface:
 *
 *     const { overBudget, fraction, loading, ensure } = useArtifiPreviewBudget()
 *     await ensure()   // triggers the (shared) fetch if not already done
 */
import { ref } from 'vue'
import axiosInstance from '@/axios'

const ENDPOINT = '/api/artifi/preview_budget'

// Module-level shared state. All callers observe the same refs so one fetch
// serves the whole page.
const _overBudget = ref(false)
const _fraction = ref(0)
const _loading = ref(false)
let _inflight = null
let _resolved = false

export function _resetArtifiPreviewBudgetCache() {
    _overBudget.value = false
    _fraction.value = 0
    _loading.value = false
    _inflight = null
    _resolved = false
}

async function _fetchOnce() {
    if (_resolved) return
    if (_inflight) return _inflight
    _loading.value = true
    _inflight = axiosInstance
        .get(ENDPOINT)
        .then((resp) => {
            const data = resp?.data || {}
            _overBudget.value = data.over_budget === true
            _fraction.value =
                typeof data.fraction === 'number' ? data.fraction : 0
            _resolved = true
        })
        .catch(() => {
            // Fail OPEN: leave overBudget false. The server-side gate is the
            // real backstop; never dark the whole page on a status blip.
            _overBudget.value = false
            _resolved = true
        })
        .finally(() => {
            _loading.value = false
            _inflight = null
        })
    return _inflight
}

export function useArtifiPreviewBudget() {
    return {
        overBudget: _overBudget,
        fraction: _fraction,
        loading: _loading,
        ensure: _fetchOnce,
    }
}

export default useArtifiPreviewBudget
